import React, { useEffect, useState, useCallback, useMemo } from "react";
import { Container, Row, Col, Spinner, Alert } from "reactstrap";

import Tiles from "./Tiles";
import TopTriggeredRules from "./TopTriggeredRules";
import SeverityDistributionChart from "./SeverityDistrubutionChart";
import DetectionsByAgent from "./DetectionsByAgents";
import DetectionsByDayHour from "./DetectionsByDayHour";
import RulesTable from "./RulesTable";

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;

// Orange color palette
const ORANGE_COLORS = {
  primary: '#FF5B00',
  dark: '#e85a28',
  darker: '#d14d1f',
  light: '#ff8c61',
  lighter: '#ffb399',
  subtle: '#fff5f0',
  muted: '#ffe8dd',
};

const pageHeadingStyle = { color: "var(--vz-body-color)" };
const toRgba = (hex, alpha) => {
  const value = hex.replace("#", "");
  const r = parseInt(value.slice(0, 2), 16);
  const g = parseInt(value.slice(2, 4), 16);
  const b = parseInt(value.slice(4, 6), 16);
  return `rgba(${r}, ${g}, ${b}, ${alpha})`;
};
const pageIconWrapStyle = {
  width: 32,
  height: 32,
  borderRadius: 8,
  border: `0.5px solid ${toRgba(ORANGE_COLORS.primary, 0.30)}`,
  background: toRgba(ORANGE_COLORS.primary, 0.10),
  display: "inline-flex",
  alignItems: "center",
  justifyContent: "center",
  flexShrink: 0,
};
const pageIconStyle = { color: ORANGE_COLORS.primary, fontSize: 16 };

// Cache duration: 5 minutes
const CACHE_DURATION = 5 * 60 * 1000;
const cache = new Map();

const RulesDetectionAnalytics = () => {
  document.title = "Rules Detection Analytics";

  // -----------------------------
  // PAGE STATE
  // -----------------------------
  const [tileStats, setTileStats] = useState({
    totalRules: null,
    activeDetections: null,
    highSeverityRules: null,
    disabledRules: null,
  });
  const [dayHourDetections, setDayHourDetections] = useState([]);
  const [severityStats, setSeverityStats] = useState(null);
  const [agentDetections, setAgentDetections] = useState([]);
  const [topRules, setTopRules] = useState([]);
  const [ruleList, setRuleList] = useState([]);

  // Progressive loading states
  const [tilesLoaded, setTilesLoaded] = useState(false);
  const [chartsLoaded, setChartsLoaded] = useState(false);
  const [tablesLoaded, setTablesLoaded] = useState(false);
  
  const [error, setError] = useState(null);

  // AUTH HEADERS
  const getAuthHeaders = useCallback(() => {
    const authUser = JSON.parse(sessionStorage.getItem("authUser"));
    return {
      "Content-Type": "application/json",
      ...(authUser?.token && { Authorization: `Bearer ${authUser.token}` }),
    };
  }, []);

  // CACHE HELPER
  const getCachedData = useCallback((key) => {
    const cached = cache.get(key);
    if (cached && Date.now() - cached.timestamp < CACHE_DURATION) {
      console.log(`✅ Cache HIT: ${key}`);
      return cached.data;
    }
    console.log(`❌ Cache MISS: ${key}`);
    return null;
  }, []);

  const setCachedData = useCallback((key, data) => {
    cache.set(key, { data, timestamp: Date.now() });
  }, []);

  // OPTIMIZED FETCH WITH CACHE
  const fetchWithCache = useCallback(async (url, cacheKey) => {
    const cached = getCachedData(cacheKey);
    if (cached) return cached;

    const response = await fetch(url, { headers: getAuthHeaders() });
    if (!response.ok) throw new Error(`HTTP ${response.status}`);
    
    const data = await response.json();
    setCachedData(cacheKey, data);
    return data;
  }, [getAuthHeaders, getCachedData, setCachedData]);

  // PHASE 1: Load Critical Data (Tiles & Rules)
  const loadTilesData = useCallback(async () => {
    try {
      console.log("📊 PHASE 1: Loading tiles and rules data...");
      
      // Parallel fetch
      const [ruleData, threatData] = await Promise.all([
        fetchWithCache(`${API_BASE_URL}/threats/rules/statistics`, 'rules-stats'),
        fetchWithCache(`${API_BASE_URL}/threats/stats`, 'threat-stats')
      ]);

      console.log("✅ Rules data:", ruleData);
      console.log("✅ Threat stats:", threatData);

      // Set tile statistics
      setTileStats({
        totalRules: ruleData.summary?.totalRules || 0,
        highSeverityRules: ruleData.summary?.highRules || 0,
        activeDetections: threatData?.stats?.active || 0,
        disabledRules: ruleData.summary?.disabledRules || 0,
      });

      // Set top 10 triggered rules
      const formattedTopRules = (ruleData.rules || [])
        .sort((a, b) => (b.triggeredCount || 0) - (a.triggeredCount || 0))
        .slice(0, 10)
        .map(r => ({
          rule: r.name,
          count: r.triggeredCount || 0
        }));
      
      console.log("📊 Top 10 Triggered Rules:", formattedTopRules);
      setTopRules(formattedTopRules);
      
      // Set full rules list for table
      setRuleList(ruleData.rules || []);
      
      setTilesLoaded(true);
      return { ruleData, threatData };

    } catch (err) {
      console.error("❌ Error loading tiles and rules:", err);
      setError("Failed to load dashboard statistics.");
      setTilesLoaded(true);
      throw err;
    }
  }, [fetchWithCache]);

  // PHASE 2: Load Chart Data from optimized analytics endpoint
  const loadChartData = useCallback(async () => {
    try {
      console.log("📈 PHASE 2: Loading analytics chart data...");
      
      // Use the new optimized analytics endpoint
      const analyticsData = await fetchWithCache(
        `${API_BASE_URL}/threats/rules/analytics`,
        'rules-analytics'
      );

      console.log("✅ Rules analytics data loaded:", analyticsData);

      if (analyticsData.success && analyticsData.analytics) {
        const { agents, dayHour, severity } = analyticsData.analytics;

        // Set agent detections
        console.log(`📊 Agent detections: ${agents?.length || 0} agents`);
        setAgentDetections(agents || []);

        // Set day/hour detections
        console.log(`📊 Day/Hour data points: ${dayHour?.length || 0} points`);
        setDayHourDetections(dayHour || []);

        // Set severity stats
        console.log("📊 Severity distribution:", severity);
        setSeverityStats(severity || {
          critical: 0,
          high: 0,
          medium: 0,
          low: 0
        });
      }

      setChartsLoaded(true);

    } catch (err) {
      console.error("❌ Error loading chart data:", err);
      // Don't fail the whole page, just show empty charts
      setAgentDetections([]);
      setDayHourDetections([]);
      setSeverityStats({ critical: 0, high: 0, medium: 0, low: 0 });
      setChartsLoaded(true);
    }
  }, [fetchWithCache]);

  // PHASE 3: Mark tables as loaded
  const loadTableData = useCallback(async () => {
    try {
      console.log("📋 PHASE 3: Tables ready");
      setTablesLoaded(true);
      console.log("✅ All data loaded successfully");
    } catch (err) {
      console.error("❌ Error in table phase:", err);
      setTablesLoaded(true);
    }
  }, []);

  // PROGRESSIVE LOADING ORCHESTRATION
  useEffect(() => {
    const loadData = async () => {
      try {
        console.log("🚀 Starting progressive data load...");
        
        // Phase 1: Load tiles and rules
        await loadTilesData();
        
        // Phase 2: Load chart data from analytics endpoint
        await loadChartData();
        
        // Phase 3: Tables ready
        await loadTableData();

      } catch (err) {
        console.error("❌ Dashboard loading failed:", err);
      }
    };

    loadData();
  }, [loadTilesData, loadChartData, loadTableData]);

  // Memoized loading state
  const isFullyLoaded = useMemo(() => 
    tilesLoaded && chartsLoaded && tablesLoaded,
    [tilesLoaded, chartsLoaded, tablesLoaded]
  );

  return (
    <div className="page-content">
      <Container fluid>
        {/* Page Title */}
        <Row className="mb-4">
          <Col>
            <div className="d-flex align-items-center gap-3">
              <div style={pageIconWrapStyle}>
                <i className="ri-shield-keyhole-line" style={pageIconStyle}></i>
              </div>
              <h4 className="mb-0" style={pageHeadingStyle}>
                Rules Detection Analytics
              </h4>
            </div>
          </Col>
        </Row>

        {/* Error Alert */}
        {error && (
          <Alert 
            style={{ 
              backgroundColor: ORANGE_COLORS.subtle, 
              color: ORANGE_COLORS.darker, 
              border: `1px solid ${ORANGE_COLORS.muted}` 
            }}
          >
            <i className="ri-alert-line me-2 fs-5"></i>
            <div>
              {error}
              <div className="mt-1 small">
                Try refreshing the page or contact support if the issue persists.
              </div>
            </div>
          </Alert>
        )}

        {/* PROGRESSIVE RENDERING */}
        
        {/* PHASE 1: Tiles */}
        {tilesLoaded ? (
          <Tiles {...tileStats} />
        ) : (
          <div className="d-flex justify-content-center py-3">
            <Spinner style={{ color: ORANGE_COLORS.primary }} /> 
            <span className="ms-2" style={{ color: ORANGE_COLORS.dark }}>Loading statistics...</span>
          </div>
        )}

        {/* PHASE 2: Charts */}
        <Row className="mt-4 mb-4 g-3">
          <Col lg={6}>
            {tilesLoaded ? (
              <TopTriggeredRules data={topRules} />
            ) : (
              <div className="card" style={{ border: `1px solid ${ORANGE_COLORS.muted}` }}>
                <div className="card-body text-center py-4">
                  <Spinner style={{ color: ORANGE_COLORS.primary }} />
                </div>
              </div>
            )}
          </Col>

          <Col lg={6}>
            {tilesLoaded ? (
              <SeverityDistributionChart severityStats={severityStats} />
            ) : (
              <div className="card" style={{ border: `1px solid ${ORANGE_COLORS.muted}` }}>
                <div className="card-body text-center py-4">
                  <Spinner style={{ color: ORANGE_COLORS.primary }} />
                </div>
              </div>
            )}
          </Col>
        </Row>

        <Row className="mt-4 mb-4 g-3">
          <Col lg={6}>
            {chartsLoaded ? (
              <DetectionsByAgent data={agentDetections} />
            ) : (
              <div className="card" style={{ border: `1px solid ${ORANGE_COLORS.muted}` }}>
                <div className="card-body text-center py-4">
                  <Spinner style={{ color: ORANGE_COLORS.primary }} /> 
                  <span className="ms-2" style={{ color: ORANGE_COLORS.dark }}>Loading agent data...</span>
                </div>
              </div>
            )}
          </Col>

          <Col lg={6}>
            {chartsLoaded ? (
              <DetectionsByDayHour data={dayHourDetections} />
            ) : (
              <div className="card" style={{ border: `1px solid ${ORANGE_COLORS.muted}` }}>
                <div className="card-body text-center py-4">
                  <Spinner style={{ color: ORANGE_COLORS.primary }} />
                </div>
              </div>
            )}
          </Col>
        </Row>

        {/* PHASE 3: Table */}
        <Row>
          <Col md={12}>
            {tablesLoaded ? (
              <RulesTable rules={ruleList} />
            ) : (
              <div className="card" style={{ border: `1px solid ${ORANGE_COLORS.muted}` }}>
                <div className="card-body text-center py-4">
                  <Spinner style={{ color: ORANGE_COLORS.primary }} /> 
                  <span className="ms-2" style={{ color: ORANGE_COLORS.dark }}>Loading rules table...</span>
                </div>
              </div>
            )}
          </Col>
        </Row>

        {/* Loading indicator */}
        {!isFullyLoaded && (
          <div className="text-center py-2" style={{ color: ORANGE_COLORS.dark }}>
            <small>Loading additional data...</small>
          </div>
        )}
      </Container>
    </div>
  );
};

export default RulesDetectionAnalytics;
